import BaseRequest from './BaseRequest';
import { API_URL } from '../config/config';
import JtokenRequest from './JtokenRequest';
import {
    sanitizeApiId,
    logSecurityEvent,
} from '../security/apiPathSecurity';
import { format } from 'date-fns';

const sanitizeTrocaPessoaBase = base => {
    if (base === null || base === undefined) {
        return null;
    }

    const baseStr = String(base).trim();
    if (!baseStr) {
        return null;
    }

    // Account switch accepts root/pergamum routes and legacy base identifiers.
    if (baseStr === '/' || baseStr === '/meupergamum' || baseStr === 'login') {
        return baseStr;
    }

    return sanitizeApiId(baseStr, { maxLength: 100 });
};

const PessoaRequest = () => {
    // Reuse the project's security utilities to sanitize IDs and build secure
    // paths. These functions implement the whitelist and pattern checks used
    // across the codebase and are maintained in `apiPathSecurity.js`.

    return {
        buscarDepartamentosPessoa() {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/departamentos',
                { headers: BaseRequest.privateHeaders() },
            );
        },
        iniciarRecuperacaoSenhaUsuario(codUsuario, recaptchaToken) {
            const params = {};

            if (recaptchaToken) params.recaptcha_token = recaptchaToken;
            const requestedAt = format(new Date(), 'yyyyMMddHHmmss');
            const chaveToken = `P3RG4@@${codUsuario}${requestedAt}`;
            params.requestedAt = requestedAt;

            return JtokenRequest(codUsuario, chaveToken)
                .then(token => {
                    if (!token) {
                        return Promise.reject(new Error('Token inválido'));
                    }
                    const sanitizedCodUsuario = sanitizeApiId(codUsuario);
                    if (!sanitizedCodUsuario) {
                        logSecurityEvent('invalid_codUsuario', { provided: String(codUsuario) });
                        return Promise.reject(new Error('Identificador de usuário inválido'));
                    }
                    const headers = Object.assign({}, BaseRequest.publicHeaders);
                    headers.Authorization = `Bearer ${token}`;

                    return BaseRequest.axiosInst.get(
                        API_URL + '/pessoas/' + sanitizedCodUsuario + '/recuperar-senha',
                        { params, headers: headers },
                    );
                });
        },
        validarCodigoRecuperacaoSenhaUsuario(codUsuario, codigoDigitado) {
            const sanitizedCodUsuario = sanitizeApiId(codUsuario);
            if (!sanitizedCodUsuario) {
                logSecurityEvent('invalid_codUsuario_validar_codigo', { provided: String(codUsuario) });
                return Promise.reject(new Error('Identificador de usuário inválido'));
            }

            return BaseRequest.axiosInst.post(
                API_URL + '/pessoas/' + sanitizedCodUsuario + '/recuperar-senha',
                {
                    code: codigoDigitado,
                },
                { headers: BaseRequest.publicHeaders },
            );
        },
        alterarSenhaUsuario(codUsuario, codigoVerificacao, senha) {
            const sanitizedCodUsuario = sanitizeApiId(codUsuario);
            if (!sanitizedCodUsuario) {
                logSecurityEvent('invalid_codUsuario_alterar_senha', { provided: String(codUsuario) });
                return Promise.reject(new Error('Identificador de usuário inválido'));
            }

            return BaseRequest.axiosInst.put(
                API_URL + '/pessoas/' + sanitizedCodUsuario + '/recuperar-senha',
                {
                    code: codigoVerificacao,
                    password: senha.trim(),
                },
                { headers: BaseRequest.publicHeaders },
            );
        },
        geraUrlAcessoMeuPergamum() {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/meu-pergamum/redirect',
                { headers: BaseRequest.privateHeaders() },
            );
        },
        renovarValidade() {
            return BaseRequest.axiosInst.get(API_URL + '/pessoas/renovar', {
                headers: BaseRequest.privateHeaders(),
            });
        },
        geraUrlAcessoBase(base) {
            const sanitizedBase = sanitizeApiId(base, { maxLength: 100 });
            if (!sanitizedBase) {
                logSecurityEvent('invalid_base_redirect', { provided: String(base) });
                return Promise.reject(new Error('Valor de base inválido'));
            }

            return BaseRequest.axiosInst.put(
                API_URL + '/pessoas/meu-pergamum/redirect-base',
                {
                    base: sanitizedBase,
                },
                { headers: BaseRequest.privateHeaders() },
            );
        },
        buscarAfastamentosAtivosUsuario() {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/afastamentos',
                {
                    headers: BaseRequest.privateHeaders(),
                },
            );
        },
        buscarInformativoUsuario() {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/meu-pergamum/informativo',
                { headers: BaseRequest.privateHeaders() },
            );
        },
        buscarDadosPessoaisMeuPergamum() {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/meu-pergamum/dados-pessoais',
                { headers: BaseRequest.privateHeaders() },
            );
        },
        atualizaDadosPessoaisMeuPergamum(dadosFormulario) {
            return BaseRequest.axiosInst.put(
                API_URL + '/pessoas/meu-pergamum/dados-pessoais',
                dadosFormulario,
                { headers: BaseRequest.privateHeaders() },
            );
        },
        geraRedirectPostagemTrabalho() {
            return BaseRequest.axiosInst.post(
                API_URL + '/pessoas/postagem-trabalho/redirect',
                {},
                { headers: BaseRequest.privateHeaders() },
            );
        },
        alterarSenhaUsuarioMeuPergamum(senhaAtual, senhaNova) {
            return BaseRequest.axiosInst.put(
                API_URL + '/pessoas/meu-pergamum/senha',
                { senhaAtual, senhaNova },
                { headers: BaseRequest.privateHeaders() },
            );
        },
        buscarNomePessoaPorUsuario(usuario) {
            const sanitizedUsuario = sanitizeApiId(usuario);
            if (!sanitizedUsuario) {
                logSecurityEvent('invalid_buscar_nome_usuario', { provided: String(usuario) });
                return Promise.reject(new Error('Identificador de usuário inválido'));
            }

            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/' + sanitizedUsuario + '/nome',
                { headers: BaseRequest.privateHeaders() },
            );
        },
        trocarUsuarioMeuPergamum(usuario) {
            return BaseRequest.axiosInst.post(
                API_URL + '/pessoas/meu-pergamum/troca-usuario',
                { usuario },
                { headers: BaseRequest.privateHeaders() },
            );
        },
        buscarLoginsPessoa() {
            return BaseRequest.axiosInst.get(API_URL + '/pessoas/login', {
                headers: BaseRequest.privateHeaders(),
            });
        },
        trocarPessoa(codigo, base) {
            const sanitizedCodigo = sanitizeApiId(codigo);
            if (!sanitizedCodigo) {
                logSecurityEvent('invalid_codigo_trocar_pessoa', { provided: String(codigo) });
                return Promise.reject(new Error('Valor de codigo inválido'));
            }

            const sanitizedBase = sanitizeTrocaPessoaBase(base);
            if (!sanitizedBase) {
                logSecurityEvent('invalid_base_trocar_pessoa', { provided: String(base) });
                return Promise.reject(new Error('Valor de base inválido'));
            }

            return BaseRequest.axiosInst.put(
                API_URL + '/pessoas/troca-login',
                {
                    codigo: sanitizedCodigo,
                    base: sanitizedBase,
                },
                { headers: BaseRequest.privateHeaders() },
            );
        },
        getTokenRedireciona(redirecionaDados) {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/token-redireciona',
                {
                    params: redirecionaDados,
                    headers: BaseRequest.publicHeaders
                },
            );
        },
        buscarPessoasDuplicadas(usuario) {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/duplicadas',
                {
                    params: { usuario },
                    headers: BaseRequest.publicHeaders
                },
            );
        },
        autenticarUsuarioLayers(usuario) {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/autenticar/app-layers',
                {
                    params: usuario,
                    headers: BaseRequest.publicHeaders
                },
            );
        },
        getEmailPessoaLogada() {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/email-pessoa-logada',
                {
                    headers: BaseRequest.privateHeaders()
                },
            );
        },
        generateAppUUId(token) {
            return BaseRequest.axiosInst.post(
                API_URL + '/pessoas/store-app-uuid',
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
        },
        authAppUuid(uuid) {
            return BaseRequest.axiosInst.post(
                API_URL + '/pessoas/auth-app-uuid',
                { uuid },
            );
        },
        generateMeuPergamumEspacosLink() {
            return BaseRequest.axiosInst.post(
                API_URL + '/pessoas/espacos/redirect',
                {},
                { headers: BaseRequest.privateHeaders() },
            );
        },
        getAuthPermissions() {
            return BaseRequest.axiosInst.get(
                API_URL + '/pessoas/auth-permissions',
                { headers: BaseRequest.privateHeaders() }
            );
        },
        getLogCatalogo() {
            return BaseRequest.axiosInst.get(
                API_URL + '/logger/generate_for_perg_sustentacao',
                { 
                    headers: BaseRequest.privateHeaders(),
                    responseType: 'blob'
                 },
            );
        },
        isUserSupport() {
            return BaseRequest.axiosInst.get(
                API_URL + '/logger/has_permition',
                { headers: BaseRequest.privateHeaders() },
            );
        },
    };
};

export default PessoaRequest();
